import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface RemoteImageProps {
  src: string | undefined;
  alt: string;
  className?: string;
  wrapperClassName?: string;
  /** Rendered when there is no src, or when the remote fetch fails. */
  fallback: ReactNode;
  loading?: 'lazy' | 'eager';
  onLoad?: (image: HTMLImageElement) => void;
}

/**
 * Every image here is third-party: favicons from arbitrary sites and presigned S3 URLs that
 * expire. Two rules apply to all of them:
 *   1. `referrerPolicy="no-referrer"` — never leak dashboard URLs to the origin site.
 *   2. an `onError` fallback — presigned URLs expire and remote favicons vanish, and a broken
 *      image icon is not an acceptable end state.
 */
export function RemoteImage({
  src,
  alt,
  className,
  wrapperClassName,
  fallback,
  loading = 'lazy',
  onLoad,
}: RemoteImageProps) {
  const [failed, setFailed] = useState(false);

  // A new src deserves a fresh attempt (e.g. after a screenshot regeneration).
  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return (
      <div className={cn('flex items-center justify-center', wrapperClassName)} aria-hidden="true">
        {fallback}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      loading={loading}
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      onLoad={onLoad ? (event) => onLoad(event.currentTarget) : undefined}
      className={className}
    />
  );
}
